import { Alert, Button, Icon } from '@kontentino/ui';
import { faBolt } from '@fortawesome/pro-regular-svg-icons/faBolt';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { useTranslation } from 'react-i18next';
import { useUser } from 'modules/user/userSelector';
import { usePageAutoScheduleMutation } from 'app/modules/posts/hooks/usePageAutoSchedule';

interface AutoScheduleNoticeProps {
  autoScheduleEnabled: boolean;
  allPostsSchedulable: boolean;
  profileName: string;
  pageId: number;
}

const AutoScheduleNotice = ({
  autoScheduleEnabled,
  allPostsSchedulable,
  profileName,
  pageId,
}: AutoScheduleNoticeProps) => {
  const { t } = useTranslation();
  const user = useUser();
  const { setAutoSchedule, isUpdating } = usePageAutoScheduleMutation();

  if (autoScheduleEnabled) {
    return allPostsSchedulable ? (
      <Alert
        variant="info"
        icon={faBolt}
        data-cy="posts_send-for-approval_auto-schedule-notice-on"
      >
        {t('approvalEmail.autoSchedule.willSchedule')}
      </Alert>
    ) : (
      <Alert
        variant="warning"
        data-cy="posts_send-for-approval_auto-schedule-notice-warning"
      >
        {t('approvalEmail.autoSchedule.missingSchedulingInfo')}
      </Alert>
    );
  }

  const isAdmin = user.role === 'admin';

  return (
    <div
      className="tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-bg-grayscale-10 tw-p-3"
      data-cy="posts_send-for-approval_auto-schedule-notice-off"
    >
      <Icon icon={faBolt} className="tw-mt-1 tw-text-grayscale-100" />
      <div className="tw-flex-1">
        <div className="tw-font-medium tw-text-grayscale-180">
          {profileName}
        </div>
        <div className="tw-text-sm tw-text-grayscale-140">
          {isAdmin
            ? t('approvalEmail.autoSchedule.offAdmin')
            : t('approvalEmail.autoSchedule.offManager')}
        </div>
      </div>
      <Button
        variant="secondary"
        size="small"
        disabled={!isAdmin}
        isLoading={isUpdating}
        iconBefore={isAdmin ? undefined : <Icon icon={faLock} />}
        onClick={() => setAutoSchedule({ pageId, enabled: true })}
        data-cy="posts_send-for-approval_auto-schedule-turn-on"
      >
        {isAdmin
          ? t('approvalEmail.autoSchedule.turnOn')
          : t('approvalEmail.autoSchedule.adminOnly')}
      </Button>
    </div>
  );
};

export default AutoScheduleNotice;
